import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
} from '@mui/material';
import moment from 'moment';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { CustomerService } from '../../services/CustomerService';
import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from '../Common/CustomSnackBar';

interface InputProps {
    customerId: number;
}

const customerService = new CustomerService();

const CustomerCookRatingsTab = (props: InputProps) => {
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRating, setSelectedRating] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchRatings = () => {
        setIsLoading(true);
        customerService
            .getCustomerCookRatings(props.customerId)
            .then((res) => {
                setData(res);
                setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
    };

    useEffect(() => {
        if (props.customerId) fetchRatings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.customerId]);

    const handleDeleteClick = (rating: any) => {
        setSelectedRating(rating);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedRating) return;
        setIsDeleting(true);
        try {
            await customerService.deleteCustomerCookRating(selectedRating.id);
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Rating deleted successfully',
                type: CustomSnackbarTypes.SUCCESS,
            });
            setDeleteDialogOpen(false);
            setSelectedRating(null);
            fetchRatings();
        } catch {
            // Error handled by Axios interceptor
        } finally {
            setIsDeleting(false);
        }
    };

    const columns: CommonListColumn[] = [
        { label: 'ID', field: 'id' },
        { label: 'Cook ID', field: 'cookId' },
        { label: 'Reference ID', field: 'referenceId' },
        { label: 'Reference Model', field: 'referenceModelName' },
        {
            label: 'Overall Rating',
            render: (row) => <Chip label={row.overallRating} size="small" color="primary" />,
        },
        {
            label: 'Created At',
            render: (row) => moment(row.createdAt).format('DD MMM YYYY hh:mm A'),
        },
        {
            label: 'Action',
            stopRowClick: true,
            render: (row) => (
                <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => handleDeleteClick(row)}
                >
                    Delete
                </Button>
            ),
        },
    ];

    return (
        <>
            {data?.averageRating !== undefined && (
                <Box p={2} pb={0} display="flex" alignItems="center" gap="8px">
                    <Typography variant="h6">Average Rating:</Typography>
                    <Chip label={data.averageRating} color="primary" />
                </Box>
            )}

            <CommonListTable
                columns={columns}
                data={data?.ratings ?? []}
                loading={isLoading}
                emptyText="No Cook Ratings Found"
            />

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Rating</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete rating ID{' '}
                        <strong>{selectedRating?.id}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        disabled={isDeleting}
                    >
                        {isDeleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CustomerCookRatingsTab;
